import React, { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

// Reusable JPEG/PNG uploader. On success, calls onUploaded(url).
export default function ImageUploadField({ label, value, onUploaded, onClear }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Sadece JPEG/PNG/WEBP dosyaları kabul edilir");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya 5MB'tan büyük olamaz");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // value can be a full URL or relative (/api/files/{id})
  const previewSrc = value
    ? value.startsWith("http") || value.startsWith("data:")
      ? value
      : `${process.env.REACT_APP_BACKEND_URL}${value}`
    : "";

  return (
    <div className="col-span-2">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-2">{label}</span>
      <div className="flex items-start gap-4">
        <div className="w-28 h-28 border border-[#0F2746]/20 bg-[#0F2746]/5 overflow-hidden flex items-center justify-center shrink-0">
          {previewSrc ? (
            <img src={previewSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-[#4A6A8F] text-center px-2">Henüz fotoğraf yok</span>
          )}
        </div>
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            className="hidden"
            data-testid="image-upload-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="image-upload-btn"
            className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] disabled:opacity-50 px-5 py-2.5 text-xs uppercase tracking-[0.2em] flex items-center gap-2"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Yükleniyor..." : value ? "Değiştir" : "JPEG/PNG yükle"}
          </button>
          {value && (
            <button
              type="button"
              onClick={onClear}
              className="ml-3 text-xs text-[#3A5170] hover:text-red-600 underline"
              data-testid="image-clear-btn"
            >
              Kaldır (siluet göster)
            </button>
          )}
          <p className="text-[10px] text-[#4A6A8F] mt-2">JPEG / PNG / WEBP — Maks. 5 MB</p>
        </div>
      </div>
    </div>
  );
}
